import React, { useState, useEffect } from 'react';
import { X, Plus, Minus } from 'lucide-react';
import type { Game } from '../types';
import { useTranslation } from '../i18n/useTranslation';

interface LiveShotsCounterProps {
  game: Game;
  onClose: () => void;
  onSave: (totalShots: number, periodShots: Game['periodShots']) => void;
}

export default function LiveShotsCounter({ game, onClose, onSave }: LiveShotsCounterProps) {
  const { t } = useTranslation();
  const [shots, setShots] = useState({
    period1: game.periodShots?.period1 || 0,
    period2: game.periodShots?.period2 || 0,
    period3: game.periodShots?.period3 || 0,
    overtime: game.periodShots?.overtime || 0
  });

  const [totalShots, setTotalShots] = useState(0);

  useEffect(() => {
    const total = Object.values(shots).reduce((sum, count) => sum + count, 0);
    setTotalShots(total);
  }, [shots]);

  const handleIncrement = (period: keyof typeof shots) => {
    setShots(prev => ({
      ...prev,
      [period]: prev[period] + 1
    }));
  };

  const handleDecrement = (period: keyof typeof shots) => {
    setShots(prev => ({
      ...prev,
      [period]: Math.max(0, prev[period] - 1)
    }));
  };

  const handleSave = () => {
    onSave(totalShots, shots);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold">{t('liveShotsCounter.title')}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="space-y-6">
          {[
            { key: 'period1' as const, label: t('liveShotsCounter.period1') },
            { key: 'period2' as const, label: t('liveShotsCounter.period2') },
            { key: 'period3' as const, label: t('liveShotsCounter.period3') },
            { key: 'overtime' as const, label: t('liveShotsCounter.overtime') }
          ].map(({ key, label }) => (
            <div key={key} className="flex items-center justify-between">
              <span className="text-gray-700 font-medium">{label}</span>
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => handleDecrement(key)}
                  className="p-1 rounded-full hover:bg-gray-100"
                  disabled={shots[key] === 0}
                >
                  <Minus className="h-5 w-5 text-gray-500" />
                </button>
                <span className="w-8 text-center font-semibold">{shots[key]}</span>
                <button
                  onClick={() => handleIncrement(key)}
                  className="p-1 rounded-full hover:bg-gray-100"
                >
                  <Plus className="h-5 w-5 text-gray-500" />
                </button>
              </div>
            </div>
          ))}

          <div className="border-t pt-4 mt-4">
            <div className="flex justify-between items-center">
              <span className="text-lg font-semibold">{t('liveShotsCounter.totalShots')}</span>
              <span className="text-lg font-bold">{totalShots}</span>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:text-gray-900"
            >
              {t('liveShotsCounter.cancel')}
            </button>
            <button
              onClick={handleSave}
              className="bg-[#1b3968] text-white px-6 py-2 rounded-md hover:bg-[#1b3968]/90"
            >
              {t('liveShotsCounter.saveShots')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}